import type { Role } from '../../types/domain'
import { offlineDb } from './db'
import type { OfflineOperationType, OutboxOperation, OutboxPayload, OutboxStatus } from './types'

export interface EnqueueInput {
  operationId?: string
  entityType: OutboxOperation['entityType']
  entityId: string
  operationType: OfflineOperationType
  payload: OutboxPayload
  baseVersion: number | null
  userId: string
  role: Role
  dependsOn?: string[]
}

export async function enqueueOperation(input: EnqueueInput): Promise<OutboxOperation> {
  const db = await offlineDb()
  const operationId = input.operationId ?? crypto.randomUUID()
  const existing = await db.get('outbox', operationId)
  if (existing) return existing
  const operation: OutboxOperation = {
    operationId,
    entityType: input.entityType,
    entityId: input.entityId,
    operationType: input.operationType,
    payload: input.payload,
    baseVersion: input.baseVersion,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
    status: 'PENDING',
    userId: input.userId,
    role: input.role,
    dependsOn: input.dependsOn ?? [],
    nextAttemptAt: null,
  }
  await db.put('outbox', operation)
  notifyOutboxChanged()
  return operation
}

export async function listOperations(userId: string, statuses?: OutboxStatus[]): Promise<OutboxOperation[]> {
  const db = await offlineDb()
  const operations = await db.getAllFromIndex('outbox', 'by_user', userId)
  return operations
    .filter((operation) => !statuses || statuses.includes(operation.status))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function countOperations(userId: string): Promise<{ pending: number; needsAttention: number }> {
  const operations = await listOperations(userId)
  return {
    pending: operations.filter((operation) => operation.status !== 'NEEDS_ATTENTION').length,
    needsAttention: operations.filter((operation) => operation.status === 'NEEDS_ATTENTION').length,
  }
}

export async function updateOperation(operation: OutboxOperation): Promise<void> {
  const db = await offlineDb()
  await db.put('outbox', operation)
  notifyOutboxChanged()
}

export async function removeOperation(operationId: string): Promise<void> {
  const db = await offlineDb()
  await db.delete('outbox', operationId)
  notifyOutboxChanged()
}

export async function hasCompletedDependencies(operation: OutboxOperation): Promise<boolean> {
  const db = await offlineDb()
  for (const dependency of operation.dependsOn) {
    if (await db.get('outbox', dependency)) return false
  }
  return true
}

export function notifyOutboxChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('requisicoes:outbox-changed'))
}

